import React, { useState, FormEvent, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { ItemRenderer, ItemPredicate } from '@blueprintjs/select';
import { FormGroup, InputGroup, Button, Tabs, Tab, Checkbox, Collapse, HTMLSelect, HTMLTable, ControlGroup, Classes, MenuItem, Alert } from "@blueprintjs/core";

import { IconNames } from "@blueprintjs/icons";

import { AxiosInstance } from 'axios';

import * as utils from "./utils";

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next';

import { Customer } from './Types'

interface CustomerViewProps {
    cust: Customer,
    baseUrl?: string,
    token?: string | null,
    onEdit?: () => void,
    onNew?: () => void,
    onDelete?: (cust: Customer) => void,
}

function CustomerView({ cust = {} as Customer, baseUrl = "", token = "", onEdit = () => { }, onNew = () => { }, onDelete = () => {} }: CustomerViewProps) {
    const { t, i18n } = useTranslation();
    const [isOpenDelete, setIsOpenDelete] = useState(false)

    if (!("custCode" in cust)) return null

    return (
        <div>
            <Alert icon="trash" intent="danger" confirmButtonText={t('button_delete')} isOpen={isOpenDelete} onConfirm={() => {onDelete(cust); setIsOpenDelete(false)}} onCancel={() => setIsOpenDelete(false)} cancelButtonText={t('button_no')} canEscapeKeyCancel={true} canOutsideClickCancel={true}>
                <p>{t('confirm_delete_customer', {cust})}</p>
            </Alert>
            <div className="Row">
                <Button className="Col-1-1" icon="add" onClick={() => onNew()}>{t('new')}</Button>
                <Button className="Col-2-1" icon="edit" onClick={() => onEdit()}>{t('edit')}</Button>
            </div>
            <div className="Spacing-V-16" />
            <div className="Row">
                <FormGroup className="Col-1-1" label={t('customer')}>
                    <InputGroup readOnly value={cust.custCode} />
                </FormGroup>
                <FormGroup className="Col-2-3" label={t('customer_name')}>
                    <InputGroup readOnly value={cust.custName} />
                </FormGroup>
            </div>
            <div className="Row">
                <FormGroup className="Col-1-1" label={t('receivable')}>
                    <InputGroup readOnly value={cust.acReceivable} />
                </FormGroup>
            </div>
            <div className="Spacing-V-16" />
            <div className="Row">
                <Button className="Col-1-1" icon="trash" onClick={() => setIsOpenDelete(!isOpenDelete)}>{t('delete')}</Button>
            </div>
        </div>
    )
}

interface CustomerEditProps {
    cust?: Customer,
    isNew?: boolean,
    onSave?: (cust: Customer) => void,
    onCancel?: () => void;
}

function CustomerEdit({ cust = {} as Customer, isNew = false, onSave = () => { }, onCancel = () => { } }: CustomerEditProps) {
    const { t, i18n } = useTranslation();
    const { register, handleSubmit, reset, setValue } = useForm<Customer>()

    useEffect(() => {
        // Call reset to set default values if cust is passed
        if ("custCode" in cust) {
            reset(cust)
        }
    }, [cust, reset])

    useEffect(() => {
        // Suggest don't have ref to use with react-hook-form, so register it manually and call setValue when item is selected
        //register({ name: "acType" })
    }, [register])

    if (!isNew && !("custCode" in cust)) return null

    const onSubmit = (data: Customer) => {
        //console.log("onsubmit called", data)
        onSave(data)
    }

    function highlightText(text: string, query: string) {
        let lastIndex = 0;
        const words = query
            .split(/\s+/)
            .filter(word => word.length > 0)
            .map(escapeRegExpChars);
        if (words.length === 0) {
            return [text];
        }
        const regexp = new RegExp(words.join("|"), "gi");
        const tokens: React.ReactNode[] = [];
        while (true) {
            const match = regexp.exec(text);
            if (!match) {
                break;
            }
            const length = match[0].length;
            const before = text.slice(lastIndex, regexp.lastIndex - length);
            if (before.length > 0) {
                tokens.push(before);
            }
            lastIndex = regexp.lastIndex;
            tokens.push(<strong key={lastIndex}>{match[0]}</strong>);
        }
        const rest = text.slice(lastIndex);
        if (rest.length > 0) {
            tokens.push(rest);
        }
        return tokens;
    }

    function escapeRegExpChars(text: string) {
        //return text.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
        return text.replace(/([.*+?^=!:${}()|\\])/g, "\\$1");
    }

    const handleHotkey = (e: React.KeyboardEvent<HTMLFormElement>) => {
        //console.log(`ctrl:${e.ctrlKey} alt:${e.altKey} shift:${e.shiftKey} meta:${e.metaKey} key:${e.key} keyCode:${e.keyCode}`)

        // ctrl + s
        if (e.ctrlKey && e.keyCode === 83) {
            e.preventDefault()
            handleSubmit(onSubmit)()
        }
    }

    return (
        <form onKeyDown={handleHotkey}>
            <div className="Row">
                <Button className="Col-1-1" icon="cross" onClick={() => onCancel()}>{t('cancel')}</Button>
                <Button className="Col-2-1" icon="tick" onClick={handleSubmit(onSubmit)}>{t('save')}</Button>
            </div>
            <div className="Spacing-V-16" />
            <div className="Row">
                <FormGroup className="Col-1-1" label={t('customer')} labelFor="inputCustCode">
                    <InputGroup readOnly={!isNew} id="inputCustCode" name="custCode" inputRef={register} />
                </FormGroup>
                <FormGroup className="Col-2-3" label={t('customer_name')} labelFor="inputCustName">
                    <InputGroup id="inputCustName" name="custName" inputRef={register} />
                </FormGroup>
            </div>
            <div className="Row">
                <FormGroup className="Col-1-1" label={t('receivable')} labelFor="inputReceivable">
                    <InputGroup id="inputReceivable" name="acReceivable" inputRef={register} />
                </FormGroup>
            </div>
        </form>
    )
}

interface CustomerMainProps {
    svc: AxiosInstance
}

function CustomerMain({ svc }: CustomerMainProps) {
    let { tabId: defaultTabId = "list" }: { tabId: string } = useParams()
    const { t, i18n } = useTranslation();
    const history = useHistory()

    const [data, setData] = useState([] as Customer[])

    const [searchKey, setSearchKey] = useState("custCode")
    const [searchValue, setSearchValue] = useState("")

    const [showFilters, setShowFilters] = useState(true)

    const [searchName, setSearchName] = useState("")
    const [searchType, setSearchType] = useState("")
    const [searchCcy, setSearchCcy] = useState("")

    const [searchLimit, setSearchLimit] = useState(0)
    const [searchBackward, setSearchBackward] = useState(false)

    const [selectedTabId, setSelectedTabId] = useState(defaultTabId) //useState("list")
    const [lastTabId, setLastTabId] = useState(defaultTabId) //useState("list")
    const [currentCust, setCurrentCust] = useState({} as Customer)

    const [currentMode, setCurrentMode] = useState("show")

    useEffect(() => {
        setSelectedTabId(defaultTabId)
    }, [defaultTabId])

    const changeTab = (tabId: string) => {
        history.push(`/customers/${tabId}`)
        setSelectedTabId(tabId)
    }

    useEffect(() => {
        (async () => {
            refreshCustomers()
        })()
    }, [svc])

    useEffect(() => {
        if (data.length > 0) {
            let i = 0;
            if ("custCode" in currentCust) {
                i = data.findIndex(cust => cust.custCode === currentCust.custCode)
            }
            setCurrentCust(i > 0 ? data[i] : data[0])
        }
    }, [data])

    const refreshCustomers = async () => {
        try {
            const res = await svc.post('/customerlist',
                {
                    by: searchKey,
                    start: searchValue,
                    limit: searchLimit,
                    backward: searchBackward,
                    filters: {
                        custName: searchName,
                        acType: searchType,
                        ccyCode: searchCcy
                    }
                });

            setData(res.data.data)
            if (res.data.data.length === 0) {
                utils.showError(t('no_record'))
            }
        } catch (error) {
            utils.showError(error)
        }    
    }

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        refreshCustomers()
    }

    const editOnSave = async (cust: Customer) => {
        try {
            const res = await svc.post('/customerupdate', cust)
            utils.showSuccess(t('saved', {key: res.data.data.custCode}))
            for (let i = 0; i < data.length; i++) {
                if (data[i].custCode === res.data.data.custCode) {
                    data[i] = { ...data[i], ...res.data.data }
                    setData(data)
                    break
                }
            }
            setCurrentCust(res.data.data)
            setCurrentMode("show")
        } catch (error) {
            utils.showError(error)
        }
    }

    const newOnSave = async (cust: Customer) => {
        try {
            const res = await svc.post('/customeradd', cust)
            utils.showSuccess(t('saved', {key: res.data.data.custCode}))
            refreshCustomers()
            setCurrentCust(res.data.data)
            setCurrentMode("show")
        } catch (error) {
            utils.showError(error)
        }
    }

    const viewOnDelete = async (cust: Customer) => {
        try {
            //console.log("deleting customer:", cust)
            const res = await svc.post('/customerdelete', cust)
            utils.showSuccess(t('deleted'))
            setData(data.filter(cust => !(cust.custCode === res.data.custCode)))
            setCurrentCust({} as Customer)
            changeTab("list")
        } catch (error) {
            utils.showError(error)
        }
    }

    // currentMode = show
    let detail = <CustomerView cust={currentCust} baseUrl={svc.defaults.baseURL} token={localStorage.getItem('token')} onEdit={() => setCurrentMode("edit")} onNew={() => {setLastTabId(selectedTabId); setCurrentMode("new")}} onDelete={viewOnDelete}></CustomerView>
    switch (currentMode) {
        case "edit":
            detail = <CustomerEdit cust={currentCust} onSave={editOnSave} onCancel={() => setCurrentMode("show")}></CustomerEdit>
            break
        case "new":
            detail = <CustomerEdit isNew onSave={newOnSave} onCancel={() => { setCurrentMode("show"); changeTab(lastTabId) }}></CustomerEdit>
            break
    }

    let list = <>
        <div className="Row">
            <Button className="Col-1-1" icon="add" onClick={() => { setLastTabId(selectedTabId); setCurrentMode("new"); changeTab("detail") }}>{t('new')}</Button>
        </div>
        <div className="Spacing-V-16" />
        <HTMLTable striped interactive condensed>
            <thead>
                <tr>
                    <th className="TCol-ACCode">{t('customer')}</th>
                    <th className="TCol-ACName">{t('customer_name')}</th>
                    <th className="TCol-ACCode">{t('receivable')}</th>
                </tr>
            </thead>
            <tbody>
                {data.map((cust, i) =>
                    <tr key={i} onDoubleClick={() => { changeTab("detail") }}>
                        <td className="TCol-ACCode" onClick={() => { setCurrentCust(cust) }}>{cust.custCode}</td>
                        <td className="TCol-ACName" onClick={() => { setCurrentCust(cust) }}>{cust.custName}</td>
                        <td className="TCol-ACCode" onClick={() => { setCurrentCust(cust) }}>{cust.acReceivable}</td>
                    </tr>
                )}
            </tbody>
        </HTMLTable>
    </>

    return (
        <>
            <div style={{fontSize: "21px", fontWeight: 700}}>{t('customers')}</div>
            <div className="Spacing-V-16"></div>
            <form onSubmit={onSubmit}>
                <div className="Row">
                    <FormGroup className="Col-1-3" label={t('search')} labelFor="inputSearchValue">
                        <ControlGroup fill>
                            <HTMLSelect className={Classes.FIXED} value={searchKey} onChange={utils.handleStringChange(s => setSearchKey(s))}>
                                <option value="custCode">{t('customer')}</option>
                                <option value="_recordNo">Record No.</option>
                            </HTMLSelect>
                            <InputGroup
                                id="inputSearchValue"
                                placeholder={t('search')}
                                value={searchValue}
                                onChange={utils.handleStringChange(s => setSearchValue(s))}
                                leftIcon="search"
                                rightElement={<Button icon={IconNames.ARROW_RIGHT} minimal type="submit"></Button>}
                            />
                        </ControlGroup>
                    </FormGroup>
                    {/*<Checkbox className="Col-4-1 Checkbox-LabeledFormGroup" label="Backward" onChange={utils.handleBooleanChange(v => setSearchBackward(v))} />*/}
                    {/*<Button className="Col-5-1 Button-LabeledFormGroup" icon="filter" onClick={() => { setShowFilters(!showFilters) }} >Filters</Button>*/}
                </div>
                <Collapse isOpen={showFilters}>
                    <div className="Row">
                        <FormGroup className="Col-1-3" label={t('customer_name')} labelFor="searchName">
                            <InputGroup id="searchName" value={searchName} onChange={utils.handleStringChange(s => setSearchName(s))} />
                        </FormGroup>
                        {/*
                        <div className="Col-4-2 Col-H">
                            <FormGroup label="Type" labelFor="searchType">
                                <InputGroup className="Input-XS" id="searchType" value={searchType} onChange={utils.handleStringChange(s => setSearchType(s))} />
                            </FormGroup>
                            <FormGroup label="Limit" labelFor="searchLimit" >
                                <InputGroup className="Input-XS" id="searchLimit" value={searchLimit.toString()} onChange={utils.handleNumberChange(n => setSearchLimit(n))} />
                            </FormGroup>
                        </div>
                        */}
                    </div>
                </Collapse>
            </form>
            <Tabs onChange={(newTabId) => changeTab(newTabId as string)} selectedTabId={selectedTabId}>
                <Tab id="list" title={t('list')} panel={list} />
                <Tab id="detail" title={t('detail')} panel={detail} />
            </Tabs>
        </>
    );
}

export default CustomerMain